import { useEffect, useState } from 'react';

// One-shot bubble fixed at the center of the *viewport* (not the document)
// that appears right after the page finishes loading, then bursts. Because
// it's position: fixed and centered regardless of scroll, it gives every
// visitor an immediate cue that there are wish bubbles on this page even
// on tall layouts where the actual bubble section sits below the fold.
//
// onPop fires at the exact moment this hero bubble bursts, so the caller
// can trigger the rest of the wish bubbles to fall in right after it (kept
// in lockstep with the hero, regardless of scroll position/visibility of
// the wish bubble section itself).
export default function HeroBubbleIntro({ dark, onPop }: { dark?: boolean; onPop?: () => void }) {
  const [stage, setStage] = useState<'hidden' | 'in' | 'pop' | 'done'>('hidden');

  useEffect(() => {
    const reduceMotion = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
    if (reduceMotion) {
      setStage('done');
      onPop?.();
      return;
    }
    const t1 = setTimeout(() => setStage('in'), 120);
    const t2 = setTimeout(() => {
      setStage('pop');
      onPop?.();
    }, 700);
    const t3 = setTimeout(() => setStage('done'), 1060);
    return () => {
      clearTimeout(t1);
      clearTimeout(t2);
      clearTimeout(t3);
    };
    // Intentionally run once on mount only; onPop is captured from the
    // initial render, which is all this one-shot intro needs.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  if (stage === 'hidden' || stage === 'done') return null;

  const shardCount = 10;
  const colors = ['#b4befe', '#a6e3a1', '#f9e2af', '#fab387', '#f38ba8', '#89b4fa'];
  const shards = Array.from({ length: shardCount }, (_, i) => {
    const angle = (360 / shardCount) * i;
    const rad = (angle * Math.PI) / 180;
    const distance = 90 + (i % 3) * 22;
    return {
      tx: Math.cos(rad) * distance,
      ty: Math.sin(rad) * distance,
      color: colors[i % colors.length],
      size: 10 + (i % 4) * 3,
      delay: (i % 3) * 20,
    };
  });

  return (
    <div className="hero-bubble-wrap" aria-hidden="true">
      <div className={`hero-bubble ${dark ? 'hero-bubble-dark' : 'hero-bubble-light'} ${stage === 'pop' ? 'hero-bubble-pop' : ''}`}>
        <span className="hero-bubble-emoji">💭</span>
      </div>
      {stage === 'pop' && (
        <div className="hero-bubble-shards">
          {shards.map((s, i) => (
            <span
              key={i}
              className="hero-shard"
              style={{
                ['--tx' as any]: `${s.tx}px`,
                ['--ty' as any]: `${s.ty}px`,
                ['--size' as any]: `${s.size}px`,
                background: s.color,
                animationDelay: `${s.delay}ms`,
              } as React.CSSProperties}
            />
          ))}
        </div>
      )}
    </div>
  );
}
